import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Users, UserPlus, Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PreviewBanner } from "@/components/PreviewBanner";

// Fabricated data, presented only as illustrative content — not the user's
// real friends. See apps/smartpoker/src/components/PreviewBanner.tsx for the
// MOCK_ naming + <PreviewBanner /> convention this pairs with.
const MOCK_FRIENDS = [
    { id: 1, name: "AceHigh", avatar: "https://i.pravatar.cc/150?u=acehigh", online: true },
    { id: 2, name: "BluffMaster", avatar: "https://i.pravatar.cc/150?u=bluffmaster", online: false },
    { id: 3, name: "RiverRat", avatar: "https://i.pravatar.cc/150?u=riverrat", online: true },
];

const MOCK_FRIEND_REQUESTS = [
    { id: 4, name: "NewbiePlayer", avatar: "https://i.pravatar.cc/150?u=newbieplayer" },
    { id: 5, name: "CardShark", avatar: "https://i.pravatar.cc/150?u=cardshark" },
];

// --- Friends Page Component ---
const Friends = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("friends");

    return (
        <div className="min-h-screen bg-background pb-24">
            <div className="p-4 space-y-6 max-w-screen-xl mx-auto">
                {/* Header */}
                <header className="flex items-center gap-4">
                    <Button asChild variant="ghost" size="icon" className="shrink-0">
                        <Link to="/profile">
                            <ArrowLeft className="w-6 h-6" />
                        </Link>
                    </Button>
                    <div className="flex items-center gap-3">
                        <div className="bg-secondary/10 p-3 rounded-xl text-secondary">
                            <Users className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t('friends_title')}</h1>
                            <p className="text-sm text-muted-foreground">{t('friends_subtitle')}</p>
                        </div>
                    </div>
                </header>

                <PreviewBanner
                    titleKey="friends_preview_title"
                    descriptionKey="friends_preview_desc"
                />

                {/* Tabs for Friends and Requests */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="friends">{t('friends_tab_friends')} <Badge variant="secondary" className="ml-2">{MOCK_FRIENDS.length}</Badge></TabsTrigger>
                        <TabsTrigger value="requests">{t('friends_tab_requests')} <Badge variant="destructive" className="ml-2">{MOCK_FRIEND_REQUESTS.length}</Badge></TabsTrigger>
                    </TabsList>

                    {/* Friends List Panel */}
                    <TabsContent value="friends" className="space-y-3 mt-4">
                        {MOCK_FRIENDS.length > 0 ? (
                            MOCK_FRIENDS.map(friend => (
                                <Card key={friend.id} className="p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Avatar>
                                            <AvatarImage src={friend.avatar} alt={friend.name} />
                                            <AvatarFallback>{friend.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-semibold">{friend.name}</p>
                                            <div className="flex items-center gap-1.5">
                                                <span className={`w-2 h-2 rounded-full ${friend.online ? 'bg-accent' : 'bg-muted-foreground'}`}></span>
                                                <p className="text-xs text-muted-foreground">{friend.online ? t('friends_status_online') : t('friends_status_offline')}</p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Could add a ... button for options later */}
                                </Card>
                            ))
                        ) : (
                            <p className="text-center text-muted-foreground py-8">{t('friends_no_friends')}</p>
                        )}
                    </TabsContent>

                    {/* Requests List Panel */}
                    <TabsContent value="requests" className="space-y-3 mt-4">
                        {MOCK_FRIEND_REQUESTS.length > 0 ? (
                            MOCK_FRIEND_REQUESTS.map(request => (
                                <Card key={request.id} className="p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Avatar>
                                            <AvatarImage src={request.avatar} alt={request.name} />
                                            <AvatarFallback>{request.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                                        </Avatar>
                                        <p className="font-semibold">{request.name}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button size="icon" className="bg-accent hover:bg-accent/90 h-9 w-9">
                                            <Check className="w-5 h-5" />
                                        </Button>
                                        <Button size="icon" variant="destructive" className="h-9 w-9">
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>
                                </Card>
                            ))
                        ) : (
                             <p className="text-center text-muted-foreground py-8">{t('friends_no_requests')}</p>
                        )}
                    </TabsContent>
                </Tabs>

                 {/* Add Friend Button */}
                 <div className="fixed bottom-28 right-4 z-50">
                     <Button size="lg" className="rounded-full shadow-lg">
                         <UserPlus className="w-5 h-5 mr-2" />
                         {t('friends_add_friend')}
                     </Button>
                 </div>
            </div>
        </div>
    );
};

export default Friends;
